import { uid } from 'uid';

/**
 * Product Service Provider - Similar to SchedulingElement pattern
 * Falls back to mock data if real service is not available
 */
export const getProductService = async () => {
  // Check if we should use real service (when backend is ready)
  const useRealService = true; // Use real service to load existing products

  if (useRealService) {
    try {
      // Import real service when backend is ready
      const productService = await import('@shared/services/productService');
      return productService.default;
    } catch (error) {
      console.warn('Real product service not available, falling back to mock:', error);
      // Fall through to mock service
    }
  }

  // Mock service implementation for testing
  return {
    async getAllProducts() {
      return [
        {
          id: 'product-1',
          name: 'Premium Raam Type A',
          description: 'Hoogwaardig raam met dubbel glas en uitstekende isolatie',
          price: 1000,
          imageUrl: 'https://via.placeholder.com/300x200/4F46E5/white?text=Raam+A',
          usps: ['Energiezuinig', 'Geluiddempend', 'Veilig'],
          categoryIds: ['cat-1'],
          options: [
            {
              id: 'color-option',
              label: 'Kleur',
              caption: 'Kies de gewenste kleur voor uw raam',
              type: 'single-choice',
              required: true,
              showOnQuote: true,
              choiceConfig: {
                choices: [
                  {
                    id: 'white',
                    label: 'Wit',
                    description: 'Klassiek wit',
                    price: 0,
                    image: 'https://via.placeholder.com/100x100/ffffff/000000?text=Wit',
                    isDefault: true,
                  },
                  {
                    id: 'brown',
                    label: 'Bruin',
                    description: 'Warme bruine tint',
                    price: 50,
                    image: 'https://via.placeholder.com/100x100/8B4513/ffffff?text=Bruin',
                  },
                  {
                    id: 'anthracite',
                    label: 'Antraciet',
                    description: 'Moderne antraciet kleur',
                    price: 75,
                    image: 'https://via.placeholder.com/100x100/2F4F4F/ffffff?text=Antraciet',
                  },
                ],
              },
            },
            {
              id: 'width-option',
              label: 'Breedte',
              caption: 'Gewenste breedte in centimeters',
              type: 'number',
              required: true,
              showOnQuote: true,
              numberConfig: {
                min: 60,
                max: 300,
                step: 10,
                basePrice: 0,
                pricePerUnit: 2.5,
                defaultValue: 120,
              },
            },
            {
              id: 'glass-option',
              label: 'Glas Type',
              caption: 'Selecteer het gewenste glas type',
              type: 'single-choice',
              required: true,
              showOnQuote: true,
              choiceConfig: {
                choices: [
                  {
                    id: 'double',
                    label: 'Dubbel Glas',
                    description: 'Standaard dubbel glas',
                    price: 0,
                    isDefault: true,
                  },
                  {
                    id: 'triple',
                    label: 'Triple Glas',
                    description: 'Extra isolerend triple glas',
                    price: 200,
                  },
                  {
                    id: 'security',
                    label: 'Veiligheidsglas',
                    description: 'Gehard veiligheidsglas',
                    price: 150,
                  },
                ],
              },
            },
          ],
        },
        {
          id: 'product-2',
          name: 'Standaard Deur Model B',
          description: 'Solide deur met moderne uitstraling en goede beveiliging',
          price: 750,
          imageUrl: 'https://via.placeholder.com/300x200/059669/white?text=Deur+B',
          usps: ['Duurzaam', 'Veilig', 'Stijlvol'],
          categoryIds: ['cat-2'],
          options: [
            {
              id: 'material-option',
              label: 'Materiaal',
              caption: 'Kies het gewenste materiaal',
              type: 'single-choice',
              required: true,
              showOnQuote: true,
              choiceConfig: {
                choices: [
                  {
                    id: 'wood',
                    label: 'Hout',
                    description: 'Natuurlijk hout',
                    price: 0,
                    isDefault: true,
                  },
                  {
                    id: 'steel',
                    label: 'Staal',
                    description: 'Robuust staal',
                    price: 100,
                  },
                  {
                    id: 'composite',
                    label: 'Composiet',
                    description: 'Moderne composiet',
                    price: 150,
                  },
                ],
              },
            },
            {
              id: 'height-option',
              label: 'Hoogte',
              caption: 'Gewenste hoogte in centimeters',
              type: 'number',
              required: true,
              showOnQuote: true,
              numberConfig: {
                min: 180,
                max: 250,
                step: 5,
                basePrice: 0,
                pricePerUnit: 1.8,
                defaultValue: 200,
              },
            },
          ],
        },
        {
          id: 'product-3',
          name: 'Luxe Schuifdeur Pro',
          description: 'Premium schuifdeur systeem met geïntegreerde beveiliging',
          price: 1500,
          imageUrl: 'https://via.placeholder.com/300x200/DC2626/white?text=Schuifdeur+Pro',
          usps: ['Premium kwaliteit', 'Automatisch', 'Smart lock'],
          categoryIds: ['cat-2', 'cat-3'],
          options: [
            {
              id: 'automation-option',
              label: 'Automatisering',
              caption: 'Kies het gewenste automatisering niveau',
              type: 'single-choice',
              required: false,
              showOnQuote: true,
              choiceConfig: {
                choices: [
                  {
                    id: 'manual',
                    label: 'Handmatig',
                    description: 'Handmatige bediening',
                    price: 0,
                    isDefault: true,
                  },
                  {
                    id: 'motor',
                    label: 'Motor',
                    description: 'Elektrische motor',
                    price: 300,
                  },
                  {
                    id: 'smart',
                    label: 'Smart System',
                    description: 'Volledige smart home integratie',
                    price: 500,
                  },
                ],
              },
            },
          ],
        },
      ];
    },

    async getProductById(productId) {
      const products = await this.getAllProducts();
      return products.find((p) => p.id === productId);
    },

    async getProductFeeds() {
      return [
        {
          id: 'feed-1',
          name: 'Ramen & Deuren Collectie',
          description: 'Premium ramen en deuren voor woningen',
          products: await this.getAllProducts(),
        },
      ];
    },

    async getCategories() {
      return [
        {
          id: 'cat-1',
          name: 'Ramen',
          slug: 'ramen',
          color: '#4F46E5',
        },
        {
          id: 'cat-2',
          name: 'Deuren',
          slug: 'deuren',
          color: '#059669',
        },
        {
          id: 'cat-3',
          name: 'Premium',
          slug: 'premium',
          color: '#DC2626',
        },
      ];
    },

    // Placeholder methods for completeness
    async addProductFeed(feed) {
      console.log('Mock: Adding product feed', feed);
      return { id: uid(), ...feed };
    },

    async updateProductFeed(feedId, feed) {
      console.log('Mock: Updating product feed', feedId, feed);
      return { id: feedId, ...feed };
    },

    async deleteProductFeed(feedId) {
      console.log('Mock: Deleting product feed', feedId);
      return { success: true };
    },

    async addProductToFeed(feedId, product) {
      console.log('Mock: Adding product to feed', feedId, product);
      return { id: uid(), ...product };
    },

    async updateProductInFeed(feedId, productId, product) {
      console.log('Mock: Updating product in feed', feedId, productId, product);
      return { id: productId, ...product };
    },

    async deleteProductFromFeed(feedId, productId) {
      console.log('Mock: Deleting product from feed', feedId, productId);
      return { success: true };
    },

    async createCategory(category) {
      console.log('Mock: Creating category', category);
      return { id: uid(), ...category };
    },

    async uploadFile(file) {
      console.log('Mock: Uploading file', file.name);
      // Return a placeholder URL
      return {
        url: `https://via.placeholder.com/300x200/4F46E5/white?text=${encodeURIComponent(file.name)}`,
      };
    },
  };
};
